import { Component, inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { AppService } from '../service/app.service';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { SharedModule } from '../shared/shared.module';
import { IconModule } from "../shared/icon/icon.module";

@Component({
    selector: 'header',
    templateUrl: './header.html',
    standalone: true,
    imports: [
    NgClass,
    RouterLink,
    FormsModule,
    MenuModule,
    TranslateModule,
    SharedModule,
    IconModule
],
})
export class HeaderComponent implements OnInit {
    private readonly appSetting = inject(AppService);
    readonly store = this.appSetting.indexState;
    search = false;
    currentPageName: string = 'Dashboard';

    // Route to page name mapping
    private readonly routePageNameMap: { [key: string]: string } = {
        '/': 'Dashboard',
        '/dashboard': 'Dashboard',
        '/errors': 'Errors',
        '/contrast-error': 'Contrast Error',
        '/alerts': 'Alerts',
        '/features': 'Features',
        '/settings': 'Settings',
        '/structural-elements': 'Structural Elements',
        '/aria-labels': 'Aria Labels',
        '/reports': 'Reports',
        '/knowledge-base': 'Knowledge Base',
        '/profile': 'Profile',
        '/user-account-settings': 'User Account Settings',
        '/mailbox': 'Mailbox', 
        '/calendar': 'Calendar',
        '/chat': 'Chat',
        '/plan': 'Upgrade Plan',
        '/ticket': 'Ticket'
        // Add more route mappings as needed
    };

    notifications = [
        {
            id: 1,
            profile: 'user-profile.jpeg',
            message: '<strong class="text-sm mr-1">John Doe</strong>invite you to <strong>Prototyping</strong>',
            time: '45 min ago',
        },
        {
            id: 2,
            profile: 'profile-34.jpeg',
            message: '<strong class="text-sm mr-1">Adam Nolan</strong>mentioned you to <strong>UX Basics</strong>',
            time: '9h Ago',
        },
        {
            id: 3,
            profile: 'profile-16.jpeg',
            message: '<strong class="text-sm mr-1">Anna Morgan</strong>Upload a file',
            time: '9h Ago',
        },
    ];
    messages = [
        {
            id: 1,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
            ),
            title: 'Congratulations!',
            message: 'Your OS has been updated.',
            time: '1hr',
        },
        {
            id: 2,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
            ),
            title: 'Did you know?',
            message: 'You can switch between artboards.',
            time: '2hr',
        },
        {
            id: 3,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
            ),
            title: 'Something went wrong!',
            message: 'Send Reposrt',
            time: '2days',
        },
        {
            id: 4,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
            ),
            title: 'Warning',
            message: 'Your password strength is low.',
            time: '5days',
        },
    ];

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private readonly sanitizer: DomSanitizer,
    ) {}

    ngOnInit() {
        // Initialize page name on component load
        this.currentPageName = this.getPageNameFromRoute(globalThis.location.pathname);

        this.setActiveDropdown();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.setActiveDropdown();
                this.currentPageName = this.getPageNameFromRoute(globalThis.location.pathname);
            }
        });
    }

    setActiveDropdown() {
        const selector = document.querySelector('ul.horizontal-menu a[routerLink="' + globalThis.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const all: any = document.querySelectorAll('ul.horizontal-menu .nav-link.active');
            for (const el of all) {
                el?.classList.remove('active');
            }
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
                if (ele) {
                    ele = ele[0];
                    setTimeout(() => {
                        ele?.classList.add('active');
                    });
                }
            }
        }
    }

    removeNotification(value: number) {
        this.notifications = this.notifications.filter((d) => d.id !== value);
    }

    removeMessage(value: number) {
        this.messages = this.messages.filter((d) => d.id !== value);
    }

    changeLanguage(item: any) {
        this.translate.use(item.code);
        this.appSetting.toggleLanguage(item);
        if (this.store()?.locale?.toLowerCase() === 'ae') {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'rtl'});
        } else {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'ltr'});
        }
        globalThis.location.reload();
    }

    private getPageNameFromRoute(path: string): string {
        const route = path.toLowerCase();
        
        // Exact match first
        if (this.routePageNameMap[route]) {
            return this.routePageNameMap[route];
        }
        
        // Partial match for nested routes
        for (const [key, value] of Object.entries(this.routePageNameMap)) {
            if (route.startsWith(key) && key !== '/') {
                return value;
            }
        }
        
        // Default fallback
        return 'Dashboard';
    }
}
